import React, { useRef, useState } from "react";

import { cn, maxSizeInMB, isFileSizeOk } from "@/lib/utils";
import { Label } from "./label";;
import { DatePicker } from "@/components/date-picker";
import { Paperclip } from "lucide-react";
import { toaster } from "../Toaster";
type InputProps = React.ComponentProps<"input"> & {
	label?: string;
	setDate?: React.Dispatch<React.SetStateAction<string>>;
	dateValue?: string;
	allowedTypes?: string;
	subtitle?: string;
	containerClassName?: string;
	iconClassName?: string;
};

const FileInput = ({
	className,
	iconClassName,
	multiple = false,
	allowedTypes,
	subtitle,
	onChange,
}: InputProps) => {
	const [files, setFiles] = useState<File[]>([]);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const ACCEPTED_FILE_TYPES = [
		".pdf",
		".jpg",
		".png",
		".jpeg",
		".doc",
		".docx",
		".xlsx",
		".xls",
		".csv",
		".eml",
		".msg",
	];

	const handleDivClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const uploadedFiles = event.target.files ?? [];
		const currentFiles: File[] = [];

		if (uploadedFiles && uploadedFiles.length > 0) {
			for (const file of uploadedFiles) {
				if (isFileSizeOk(file.size)) {
					const fileName = file.name.split(".").pop() || "";

					if (
						(allowedTypes &&
							allowedTypes
								.split(", ")
								?.includes("." + fileName.toLowerCase())) ||
						(!allowedTypes &&
							ACCEPTED_FILE_TYPES.includes("." + fileName.toLowerCase()))
					) {
						currentFiles.push(file);
					} else {
						toaster(
							"error",
							`Only ${allowedTypes ? allowedTypes : ACCEPTED_FILE_TYPES.join(", ")
							} file types are allowed.`
						);
						return;
					}
				} else {
					toaster(
						"error",
						`File ${file.name} exceeds the maximum allowed size of ${maxSizeInMB}MB. Please choose a smaller file.`
					);
					return;
				}
			}

			// If multiple is disabled, replace files; otherwise append
			if (multiple) {
				setFiles((existingFiles) => [...existingFiles, ...currentFiles]);
			} else {
				setFiles(currentFiles.slice(0, 1)); // Only keep first file
			}

			if (onChange) {
				onChange(event);
			}

			// Reset input value to allow selecting the same file again
			event.target.value = '';
		}
	};

	return (
		<>
			<div
				className={cn(
					"flex flex-col space-y-1 items-center justify-center cursor-pointer",
					"border-gray-200 dark:border-gray-500 file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex h-40 w-full min-w-0 rounded-md border bg-transparent px-3 py-0 text-base transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-md file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-lg",
					"focus-visible:border-primary focus-visible:ring-ring/30 focus-visible:ring-[1px]",
					"aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
					className
				)}
				onClick={handleDivClick}
			>
				<div>
					<Paperclip size={60} className={iconClassName} />
				</div>
				<div className="text-sm">
					{subtitle ? subtitle : "You can attach supporting files"}
				</div>
				<div className="text-sm text-gray-400">
					Supported File Formats: (
					{allowedTypes ? allowedTypes : ACCEPTED_FILE_TYPES.join(", ")})
				</div>
			</div>
			<input
				type="file"
				multiple={multiple}
				ref={fileInputRef}
				style={{ display: "none" }}
				onChange={handleFileChange}
			/>

			{!!files.length && (
				<div className="space-y-2 mt-4">
					<h4>Attached files:</h4>
					<div className="flex gap-3">
						{files.map((x, i) => (
							<div
								key={x.name + i}
								className="border border-gray-300 p-2 rounded bg-gray-200 dark:border-gray-600 dark:bg-gray-500/10"
							>
								{i + 1}. {x.name}
							</div>
						))}
					</div>
				</div>
			)}
		</>
	);
};

function Input({
	className,
	type,
	label,
	dateValue,
	setDate,
	containerClassName,
	iconClassName,
	...props
}: InputProps) {
	return (
		<div className={containerClassName}>
			{!!label && (
				<div className="my-3 capitalize">
					<Label>{label}</Label>
				</div>
			)}
			{type == "file" ? (
				<FileInput className={className} iconClassName={iconClassName} {...props} />
			) : type == "date" && setDate ? (
				<DatePicker
					setDate={setDate}
					dateValue={dateValue}
					className={cn("w-full", className)}
					{...props}
				/>
			) : type == "datetime" && setDate ? (
				<DatePicker
					setDate={setDate}
					dateValue={dateValue}
					time
					className={cn("w-full", className)}
				/>
			) : (
				<input
					type={type}
					data-slot="input"
					className={cn(
						"border-gray-500 dark:border-gray-500 file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex h-12 w-full min-w-0 rounded-md border bg-transparent px-3 py-0 text-base transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-md file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
						"focus-visible:border-primary focus-visible:ring-ring/30 focus-visible:ring-[1px]",
						"aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
						type === "password" && "font-tl-password",
						className,
						props.disabled && "border-gray-200 bg-gray-100 dark:bg-gray-500/50 disabled:cursor-not-allowed",
					)}
					{...props}
				/>
			)}
		</div>
	);
}

export { Input };
